import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { OrganizationService } from '../organization.service';
import { OrganizationBankAccountsAddEditComponent } from '@/organization/organization/organization-bank-accounts/organization-bank-accounts-add-edit/organization-bank-accounts-add-edit.component';
import { catchError, filter, forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { OrganizationBankAccountsApiService } from '@/organization/organization/organization-bank-accounts/organization-bank-accounts-api.service';
import {
	BankAccount,
	BulkUpdateBankAccountPayload,
	OrganizationBankAccountDialogData,
	OrganizationBankAccountMode,
} from '@/organization/organization/organization-bank-accounts/organization-bank-accounts.model';
import { InfoboxService } from '@/common/infobox/infobox.service';
import {
	BankAccountAssignDialogData,
	OrganizationBankAccountAssignDialogComponent,
} from './organization-bank-account-assign-dialog/organization-bank-account-assign-dialog.component';
import { MassBankAccountCreateDialogComponent } from './mass-bank-account-create-dialog/mass-bank-account-create-dialog.component';
import { MassBankAccountDuplicateDialogComponent } from './mass-bank-account-duplicate-dialog/mass-bank-account-duplicate-dialog.component';
import { ibanCurrencyCode } from '@/common/validators/iban.validator';

export const BANK_ACCOUNT_ERROR_MESSAGE = 'bank account with this number already exists';

const DEFAULT_CURRENCY = 1; // This is PLN currency
const DEFAULT_KIND = 1; // This is PLN currency kind

@Component({
	selector: 'app-organization-bank-accounts',
	templateUrl: './organization-bank-accounts.component.html',
	styleUrls: ['./organization-bank-accounts.component.css'],
})
export class OrganizationBankAccountsComponent implements OnInit {
	dataTableBankAccounts = new MatTableDataSource<BankAccount>();
	@ViewChild('bankAccountsTable') table: MatTable<BankAccount>;
	bankAccountsColumns: string[] = [
		'index',
		'isActive',
		'currency',
		'number',
		'swift',
		'fastAlior',
		'mainAccount',
		'edit',
	];

	protected readonly OrganizationBankAccountMode = OrganizationBankAccountMode;

	constructor(
		private readonly organizationService: OrganizationService,
		private readonly dialog: MatDialog,
		private readonly organizationBankAccountsApiService: OrganizationBankAccountsApiService,
		private readonly infoboxService: InfoboxService,
	) {}

	ngOnInit(): void {
		if (!this.organizationService.organizationData.id) {
			return;
		}
		this.getOrganizationBankAccounts();
	}

	openBankAccountDialog(mode: OrganizationBankAccountMode, bankAccount?: BankAccount): void {
		if (!mode) {
			return;
		}

		const dialogData: OrganizationBankAccountDialogData = {
			mode: mode,
			bankAccount: mode === OrganizationBankAccountMode.EDIT ? bankAccount : null,
		};

		this.dialog
			.open(OrganizationBankAccountsAddEditComponent, {
				data: dialogData,
				minWidth: '380px',
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe((dialogResult: OrganizationBankAccountDialogData) => {
				const bankAccountRequest$ =
					dialogResult.mode === OrganizationBankAccountMode.ADD
						? this.organizationBankAccountsApiService.addOrganizationBankAccount(dialogResult.bankAccount)
						: this.organizationBankAccountsApiService.editOrganizationBankAccount(
								dialogResult.bankAccount.id,
								dialogResult.bankAccount,
							);

				bankAccountRequest$
					.pipe(
						catchError((err) => {
							if (JSON.stringify(err).includes(BANK_ACCOUNT_ERROR_MESSAGE)) {
								this.checkIfBankAccountAssigned(dialogResult.bankAccount.number);
							}
							return of(null);
						}),
					)
					.subscribe((result) => {
						if (result) {
							this.getOrganizationBankAccounts();
							this.table.renderRows();
						}
					});
			});
	}

	openMassBankAccountDialog(): void {
		this.dialog
			.open(MassBankAccountCreateDialogComponent, {
				width: '500px',
				disableClose: true,
			})
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe((bankAccountsNumber: string[]) => {
				const payload = this.createBulkPayload(bankAccountsNumber);

				this.organizationBankAccountsApiService
					.bankAccountBulkCreate(payload)
					.pipe(
						catchError((err) => this.handleBulkCreateError(err, payload)),
						filter(Boolean),
					)
					.subscribe(() => {
						this.reloadBankAccounts();
						this.infoboxService.success('Poprawnie dodano konta bankowe');
					});
			});
	}

	private createBulkPayload(numbers: string[]): BulkUpdateBankAccountPayload[] {
		return numbers.map((number) => ({
			kind: DEFAULT_KIND,
			currency: this.resolveCurrencyId(number),
			number,
			organization: this.organizationService.organizationData.id,
		}));
	}

	private resolveCurrencyId(iban: string): number {
		const code = ibanCurrencyCode(iban);
		if (!code) {
			return DEFAULT_CURRENCY;
		}

		const currencies = (this.organizationService.enterpriseData.currency ?? []) as { id: number; code: string }[];
		return currencies.find((currency) => currency.code === code)?.id ?? DEFAULT_CURRENCY;
	}

	private handleBulkCreateError(err: { error: unknown }, payload: BulkUpdateBankAccountPayload[]): Observable<null> {
		if (JSON.stringify(err).includes(BANK_ACCOUNT_ERROR_MESSAGE)) {
			const duplicateIndexes = this.extractDuplicateIndexes(err.error);
			const accountsWithError = payload.filter((_, i) => duplicateIndexes.includes(i));
			return this.resolveDuplicates(accountsWithError);
		}

		return of(null);
	}

	private extractDuplicateIndexes(error: unknown): number[] {
		return (
			Object.entries(error)
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				.filter(([_, value]) => JSON.stringify(value['number']).includes(BANK_ACCOUNT_ERROR_MESSAGE))
				.map(([key]) => Number(key))
		);
	}

	private resolveDuplicates(accounts: BulkUpdateBankAccountPayload[]): Observable<null> {
		return this.getBankAccountsByNumber(accounts.map((a) => a.number)).pipe(
			map((arrays) => arrays.flat()),
			switchMap((results) =>
				this.dialog
					.open<MassBankAccountDuplicateDialogComponent, BankAccount[]>(
						MassBankAccountDuplicateDialogComponent,
						{ data: results },
					)
					.afterClosed()
					.pipe(
						filter(Boolean),
						map((accounts: BankAccount[]) => accounts.map((a) => a.id)),
						switchMap((ids) => this.patchBankAccounts(ids)),
					),
			),
		);
	}

	private getBankAccountsByNumber(bankAccountsNumbers: string[]): Observable<BankAccount[][]> {
		const requests = bankAccountsNumbers.map((id) =>
			this.organizationBankAccountsApiService.getOrganizationBankAccounts(`?number=${id}`),
		);
		return forkJoin(requests);
	}

	private patchBankAccounts(ids: number[]): Observable<null> {
		const orgId = this.organizationService.organizationData.id;
		const requests = ids.map((id) =>
			this.organizationBankAccountsApiService.patchOrganizationBankAccount(id, { organization: orgId }),
		);

		return forkJoin(requests).pipe(
			tap(() => {
				this.reloadBankAccounts();
				this.infoboxService.success('Poprawnie dodano konta bankowe');
			}),
			map(() => null),
		);
	}

	private reloadBankAccounts(): void {
		this.getOrganizationBankAccounts();
		this.table.renderRows();
	}

	private getOrganizationBankAccounts(): void {
		this.organizationBankAccountsApiService
			.getOrganizationBankAccounts(`?limit=999&organization_id=${this.organizationService.organizationData.id}`)
			.subscribe((bankAccounts) => {
				this.organizationService.enterpriseData.bankAccounts = bankAccounts;
				this.addColumnsToBankAccountsTable(bankAccounts);
				this.checkIsMainAccountSet();
			});
	}

	private checkIsMainAccountSet(): void {
		if (
			!this.organizationService.enterpriseData.bankAccounts ||
			!this.organizationService.enterpriseData.bankAccounts.length
		) {
			return;
		}

		this.organizationService.enterpriseData.mainAccount =
			this.organizationService.enterpriseData.bankAccounts.filter((v) => v.mainAccount);
	}

	private addColumnsToBankAccountsTable(bankAccounts: BankAccount[]): void {
		if (!bankAccounts && !bankAccounts.length) {
			return;
		}
		this.dataTableBankAccounts.data = bankAccounts;
	}

	private checkIfBankAccountAssigned(accountNumber: string): void {
		this.organizationBankAccountsApiService
			.getOrganizationBankAccounts(`?number=${accountNumber}`)
			.subscribe((bankAccounts: BankAccount[]) => {
				if (bankAccounts.every((b) => !!b.organization)) {
					const { shortName, organization } = bankAccounts[0];
					this.infoboxService.warning(
						`Konto jest przypisane do organizacji ${shortName} (ID: ${organization})`,
					);
					return;
				}

				this.promptAssignOrganization(bankAccounts[0].id);
			});
	}

	private promptAssignOrganization(accountId: number): void {
		const organization = this.organizationService.organizationData;

		this.dialog
			.open<OrganizationBankAccountAssignDialogComponent, BankAccountAssignDialogData>(
				OrganizationBankAccountAssignDialogComponent,
				{
					data: {
						organization: {
							id: organization.id,
							name: organization.shortName,
						},
					},
					minWidth: '380px',
				},
			)
			.afterClosed()
			.pipe(
				filter(Boolean),
				switchMap(() =>
					this.organizationBankAccountsApiService
						.patchOrganizationBankAccount(accountId, {
							organization: organization.id,
						})
						.pipe(filter(Boolean)),
				),
			)
			.subscribe(() => {
				this.infoboxService.success('Pomyślnie przypisano organizację.');
				this.getOrganizationBankAccounts();
				this.table.renderRows();
			});
	}
}
